// What? A modal component for requesting access to an organization.
// Why? To separate modal logic from the main dashboard.
// How? Renders a form modal for access requests.

import React from "react";
import {
  Modal,
  ModalDialog,
  DialogTitle,
  Stack,
  FormControl,
  FormLabel,
  Textarea,
  Button,
  Alert,
  Typography,
} from "@mui/joy";

interface AvailableOrganization {
  organization_id: string;
  organization_name: string;
  verified_domains: string[];
}

interface RequestAccessModalProps {
  open: boolean;
  onClose: () => void;
  organization: AvailableOrganization | null;
  message: string;
  onMessageChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  requesting: boolean;
  error: string | null;
}

const RequestAccessModal: React.FC<RequestAccessModalProps> = ({
  open,
  onClose,
  organization,
  message,
  onMessageChange,
  onSubmit,
  requesting,
  error,
}) => {
  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        size="md"
        variant="outlined"
        sx={{
          maxWidth: 500,
          width: "100%",
        }}
      >
        <DialogTitle>Request Access</DialogTitle>

        <form onSubmit={onSubmit}>
          <Stack spacing={3}>
            {error && (
              <Alert color="danger" variant="soft">
                {error}
              </Alert>
            )}

            <Alert variant="soft" color="neutral">
              <Typography level="body-sm">
                Requesting access to{" "}
                <strong>{organization?.organization_name}</strong>
              </Typography>
            </Alert>

            <FormControl>
              <FormLabel>Message (Optional)</FormLabel>
              <Textarea
                placeholder="I'm a new engineer joining the sales team..."
                value={message}
                onChange={(e) => onMessageChange(e.target.value)}
                disabled={requesting}
                size="md"
                minRows={3}
              />
              <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                Add a message to help the admin understand why you need access.
              </Typography>
            </FormControl>

            <Stack
              direction="row"
              spacing={2}
              sx={{ justifyContent: "flex-end", mt: 2 }}
            >
              <Button
                variant="outlined"
                color="neutral"
                onClick={onClose}
                disabled={requesting}
                size="md"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="solid"
                color="primary"
                disabled={requesting}
                loading={requesting}
                size="md"
              >
                Request Access
              </Button>
            </Stack>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
};

export default RequestAccessModal;
